/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiButtonIcon,
  EuiButtonIconProps,
  EuiPopover,
  EuiPopoverProps,
  useEuiTheme,
} from "@inscopix/ideas-eui";
import { Tooltip } from "components/Tooltip/Tooltip";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import moment from "moment";
import { useState } from "react";
import { formatDate } from "utils/formatDate";

export interface ButtonContainerInfoProps
  extends
    Pick<EuiButtonIconProps, "iconSize" | "css" | "color">,
    Pick<EuiPopoverProps, "anchorPosition" | "repositionOnScroll"> {
  dateCreated: string;
  dateUpdated: string;
  user: string | null;
}

export const ButtonContainerInfo = ({
  user,
  dateCreated,
  dateUpdated,
  anchorPosition,
  repositionOnScroll,
  ...props
}: ButtonContainerInfoProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { euiTheme } = useEuiTheme();
  const styles = {
    dl: css`
      display: grid;
      grid-template-columns: max-content auto;
    `,
    dt: css`
      grid-column-start: 1;
      margin-right: 0.5rem;
      font-weight: bold;
      padding: 5px 0px;
    `,
    dtVertical: css`
      font-weight: bold;
      padding: 5px 0px 0px 0px;
    `,
    dd: css`
      padding: 5px 0px;
    `,
    ddVertical: css`
      padding: 0px 0px 5px 0px;
    `,
    subContent: css`
      color: ${euiTheme.colors.subduedText};
    `,
  };

  return (
    <Tooltip content="View Container Info">
      <EuiPopover
        title=""
        button={
          <EuiButtonIcon
            aria-label="Project info"
            iconType="iInCircle"
            color="primary"
            iconSize="l"
            {...props}
          />
        }
        repositionOnScroll={repositionOnScroll}
        isOpen={isOpen}
        closePopover={() => setIsOpen(false)}
        onClick={() => setIsOpen((isOpen) => !isOpen)}
        anchorPosition={anchorPosition ?? "upLeft"}
      >
        <dl css={styles.dl}>
          <dt css={styles.dt}>Author</dt>
          <dd css={styles.dd}>
            {user !== null ? (
              <UserAvatar userId={user} size="s" displayName />
            ) : (
              "Unknown"
            )}
          </dd>

          <dt css={styles.dt}>Created</dt>
          <dd css={styles.dd}>
            {moment(dateCreated).fromNow()}
            <br />
            <span css={styles.subContent}>{formatDate(dateCreated)}</span>
          </dd>

          <dt css={styles.dt}>Updated</dt>
          <dd css={styles.dd}>
            {moment(dateUpdated).fromNow()}
            <br />
            <span css={styles.subContent}>{formatDate(dateUpdated)}</span>
          </dd>
        </dl>
      </EuiPopover>
    </Tooltip>
  );
};
